"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStudio } from "@/contexts/studio-context";
import { useHintPublisher } from "@/hooks/use-hint-publisher";
import { currentGroupHints } from "@/hooks/journey/journey-hint-mapping";
import { useRollScrub } from "@/hooks/journey/use-roll-scrub";
import type { Hint } from "@/contexts/hint-context";
import type { SequenceGroup } from "@/lib/learning-journey/sequence-matcher";
import type { Phrase } from "@/lib/learning-journey/types";
import {
  heldKeysAt,
  passEndTick,
  pressDiagnosis,
  type HeldKey,
  type PracticeGroup,
} from "@/components/learn-piece/practice";
import {
  groupIndexAtTick,
  type PracticePass,
} from "@/components/learn-piece/mastery";

/**
 * The life of a practice run, shared by the single-hand runs and the
 * both-hands run.
 *
 * Everything here is the same whatever is being practised: walking the
 * groups, holding a validated one on screen before the swap, diagnosing a
 * wrong key, counting passes, driving the roll, publishing the hint. What
 * differs between the two screens is what they *draw*, and that stays in the
 * components.
 *
 * Extracted at the moment the both-hands step was split out: without it the
 * split would have copied two hundred lines of identical lifecycle, and the
 * two copies would have drifted at the first fix applied to one of them.
 *
 * ## Scrolling the roll takes the run with it
 *
 * The run walks the passage from its first attack, and the roll follows it.
 * The player could already scroll the roll and nothing read the gesture, so
 * working the fourth bar of a four-bar unit cost three bars on every
 * repetition. It reads it now: the run resumes at the attack the player
 * parked under the playhead. See `useRollScrub` — and note that the
 * application's own scrolling, which happens on every attack, is silent.
 *
 * ## What a finished pass leaves behind
 *
 * `lastPass` — how many attacks it walked, how many of them gave trouble, and
 * the gaps between them. That is what the two-hand step reads to decide the
 * notes are there, and at what tempo the unit should meet the metronome. See
 * `mastery.ts`; nothing here has an opinion about it.
 */

const HINT_SOURCE = "journey-practice";

/** How long a validated group stays on screen, lit, before the swap. */
const FLASH_MS = 160;

export type PracticeRun = {
  /** The group the matcher is waiting for, `undefined` once the run is done. */
  readonly currentGroup: PracticeGroup | undefined;
  /** The group on screen — the validated one during the flash, else the current. */
  readonly shownGroup: PracticeGroup | undefined;
  readonly currentIndex: number;
  readonly total: number;
  /** 1-based position of the group on screen. */
  readonly position: number;
  readonly percent: number;
  readonly completed: boolean;
  readonly passes: number;
  readonly diagnosis: string | null;
  readonly flashing: boolean;
  /** Keys sounding that the current group does not ask for. */
  readonly held: readonly HeldKey[];
  /** Remount key for `SequenceWatcher`, bumped on every retry. */
  readonly runId: number;
  /**
   * Distinct groups of the current pass on which a wrong key was pressed,
   * and how many times the player has started over without finishing.
   *
   * Together they are what a screen may read as "this is not going well" —
   * an observed failure, never a guess from the texture of the music. Both
   * reset once a pass completes.
   */
  readonly wrongGroupsThisPass: number;
  readonly retries: number;
  /**
   * The pass that just finished, or `null` before the first one and after a
   * retry. See `PracticePass`; read by the two-hand step and nobody else.
   */
  readonly lastPass: PracticePass | null;
  /**
   * Group the sequence is taken up at. `0` unless the player scrolled the
   * roll to somewhere else in the passage.
   *
   * Handed to `SequenceWatcher` as `startIndex`, and deliberately not the
   * live index: it only moves when the player asks for it.
   */
  readonly resumeIndex: number;
  /**
   * Passes the player gave up on part-way through, in this run.
   *
   * Not the same act as « Encore une fois » on the card that closes a pass,
   * even though both land on `onRetry`: one is the next repetition, the other
   * is a passage that was not coming out. The two-hand step counts a run of
   * passes that hold, and it cannot tell them apart from the outside — from
   * here they are one `lastPass` going back to `null`.
   */
  readonly abandons: number;
  readonly onRetry: () => void;
  readonly onGroupConfirmed: (index: number) => void;
  readonly onSequenceCompleted: () => void;
  readonly onError: (midi: number) => void;
  readonly onCurrentIndexChange: (index: number) => void;
  /** Groups reduced to what the matcher needs. */
  readonly sequenceGroups: readonly SequenceGroup[];
};

export function usePracticeRun(
  phrase: Phrase,
  groups: readonly PracticeGroup[],
  noteLabel: (midi: number) => string,
): PracticeRun {
  const { studio } = useStudio();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [passes, setPasses] = useState(0);
  const [diagnosis, setDiagnosis] = useState<string | null>(null);
  /**
   * The group just validated, held on screen for `FLASH_MS` before the
   * keyboard swaps to the next one. Without the hold the flash would light
   * the *incoming* keys — telling the player "that's it" about keys they
   * have not played yet.
   */
  const [confirmedIndex, setConfirmedIndex] = useState<number | null>(null);
  const [runId, setRunId] = useState(0);
  const [wrongGroupCount, setWrongGroupCount] = useState(0);
  const [retries, setRetries] = useState(0);
  const [lastPass, setLastPass] = useState<PracticePass | null>(null);
  const [resumeIndex, setResumeIndex] = useState(0);
  const [abandons, setAbandons] = useState(0);

  const completedRef = useRef(false);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Wall-clock instant of each group confirmed since the pass started.
   *
   * A ref and not state: nothing on screen reads it, and a `setState` per
   * attack next to the four this hook already fires would re-render the panel
   * for a value it never draws. What reads it is `lastPass`, once.
   */
  const confirmedAtRef = useRef<number[]>([]);
  /** Where the current pass started walking, for `PracticePass.groups`. */
  const passFromRef = useRef(0);
  /**
   * Groups of the current pass a wrong key was struck on, counted once each.
   *
   * The set is the ref and the size is the state: the card that closes the
   * pass reads the set at a moment no render is happening, and a `useState`
   * updater is not the place to read one from — it may run twice.
   */
  const wrongGroupsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    return () => {
      if (flashTimerRef.current !== null) {
        clearTimeout(flashTimerRef.current);
      }
    };
  }, []);

  const onRetry = useCallback(() => {
    // Pressed on the card that closes a pass, this is the next repetition.
    // Pressed from the band, the pass was still running and the player left
    // it. Only the second one is a passage that did not come out.
    if (!completedRef.current) {
      setAbandons((n) => n + 1);
    }

    completedRef.current = false;
    confirmedAtRef.current = [];
    wrongGroupsRef.current = new Set();
    passFromRef.current = 0;
    setCurrentIndex(0);
    setResumeIndex(0);
    setCompleted(false);
    setDiagnosis(null);
    setConfirmedIndex(null);
    setWrongGroupCount(0);
    setLastPass(null);
    setRetries((n) => n + 1);
    setRunId((id) => id + 1);
  }, []);

  /**
   * Take the passage up at the attack the player scrolled to.
   *
   * A restart and not a jump: the counters of the pass are cleared, because
   * what follows is a pass over a different stretch and reporting it against
   * the groups walked before would be reporting on two things at once. The
   * repetition count is left alone — those passes happened.
   */
  const resumeAt = useCallback((index: number) => {
    completedRef.current = false;
    confirmedAtRef.current = [];
    wrongGroupsRef.current = new Set();
    passFromRef.current = index;
    setCurrentIndex(index);
    setResumeIndex(index);
    setCompleted(false);
    setDiagnosis(null);
    setConfirmedIndex(null);
    setWrongGroupCount(0);
    setLastPass(null);
    setRunId((id) => id + 1);
  }, []);

  const onGroupConfirmed = useCallback((index: number) => {
    confirmedAtRef.current.push(performance.now());
    setCurrentIndex(index + 1);
    setDiagnosis(null);
    setConfirmedIndex(index);

    if (flashTimerRef.current !== null) {
      clearTimeout(flashTimerRef.current);
    }

    flashTimerRef.current = setTimeout(() => {
      setConfirmedIndex(null);
    }, FLASH_MS);
  }, []);

  const onSequenceCompleted = useCallback(() => {
    if (completedRef.current) {
      return;
    }

    completedRef.current = true;
    setCompleted(true);
    setPasses((n) => n + 1);
    setRetries(0);
    setLastPass({
      groups: groups.length - passFromRef.current,
      wrongGroups: wrongGroupsRef.current.size,
      intervals: intervalsOf(confirmedAtRef.current),
    });
    wrongGroupsRef.current = new Set();
    confirmedAtRef.current = [];
    setWrongGroupCount(0);
  }, [groups.length]);

  const currentGroup = groups[currentIndex];

  // Keys still sounding that this group does not ask for. A fact about the
  // score (see `heldKeysAt`): the keyboard paints them at the foot, the copy
  // names whose turn it is, and the diagnosis can answer a re-struck note.
  const held = useMemo(
    () => (currentGroup ? heldKeysAt(phrase, currentGroup) : []),
    [phrase, currentGroup],
  );
  const heldMidis = useMemo(() => new Set(held.map((k) => k.midi)), [held]);

  const onError = useCallback(
    (midi: number) => {
      const expected = currentGroup?.midis ?? [];
      const next = groups[currentIndex + 1]?.midis ?? [];

      setDiagnosis(pressDiagnosis(midi, expected, next, heldMidis, noteLabel));

      if (!wrongGroupsRef.current.has(currentIndex)) {
        wrongGroupsRef.current.add(currentIndex);
        setWrongGroupCount(wrongGroupsRef.current.size);
      }
    },
    [currentGroup, groups, currentIndex, heldMidis, noteLabel],
  );

  /**
   * The player parked an attack under the playhead: the run takes the
   * passage up there.
   *
   * Nearest attack rather than next, so scrolling onto a note lands on it
   * (`groupIndexAtTick`). A scroll that lands on the attack already current
   * changes nothing, which is what makes reading ahead free: the player can
   * look at what is coming and come back without the run noticing.
   */
  const handleScrub = useCallback(
    (tick: number) => {
      if (groups.length === 0) {
        return;
      }

      const index = groupIndexAtTick(groups, tick);

      if (index === currentIndex && !completed) {
        return;
      }

      resumeAt(index);
    },
    [groups, currentIndex, completed, resumeAt],
  );

  useRollScrub(true, handleScrub);

  useEffect(() => {
    if (!studio) {
      return;
    }

    if (completed || !currentGroup) {
      // Without this the roll kept the last group lit while the panel and
      // the on-screen piano had already gone quiet: two surfaces
      // contradicting each other at the exact moment of validation.
      studio.clearHintSelection();

      if (!completed) {
        return;
      }

      // The last attack moves the roll on like every other one: without it,
      // « Passage réussi » came up over a roll that had not moved, and the
      // player could not tell the last key had counted. See `passEndTick`.
      //
      // Then back to the start of the unit, while the card is up: the next
      // thing on offer is another pass, and it starts there.
      let cancelled = false;
      const end = passEndTick(phrase, groups);

      void (async () => {
        if (end !== null) {
          await studio.animateScrollToTick(end);
        }

        // A retry started during the first scroll has already sent the roll
        // where it wants it; going to the start on top of it would fight it.
        if (cancelled) {
          return;
        }

        await studio.animateScrollToTick(phrase.startTick);
      })();

      return () => {
        cancelled = true;
      };
    }

    void studio.animateScrollToTick(currentGroup.startTick);

    // The struck keys, and only those.
    //
    // The both-hands run used to send everything still sounding, so the roll
    // lit, at full strength and with no distinction, keys the player must
    // *not* re-strike: a false instruction on 48.0% of its groups.
    // `HintSelection.isHighlighted` is a boolean, so telling the two apart
    // there is renderer work; the panel carries the distinction instead (see
    // `KeyFoot`), and the roll now says the same thing in every run.
    studio.setHintSelection(
      [...currentGroup.midis],
      currentGroup.startTick,
      currentGroup.startTick,
    );
  }, [studio, currentGroup, completed, phrase, groups]);

  /**
   * Frame the unit on the roll for the life of the step.
   *
   * The run in time does this already; the practice steps did not, so the
   * only thing separating the unit from the rest of the piece was the
   * dimming of everything that was not the current attack. That dimming now
   * leaves the passage readable, which is what makes reading ahead possible
   * and takes the framing job off it. See `piano-roll/work-range.ts`.
   */
  useEffect(() => {
    if (studio === null) {
      return;
    }

    studio.setWorkRange({
      startTick: phrase.startTick,
      endTick: phrase.endTick,
    });

    return () => {
      studio.setWorkRange(null);
    };
  }, [studio, phrase.startTick, phrase.endTick]);

  useEffect(() => {
    if (!studio) {
      return;
    }

    return () => {
      studio.clearHintSelection();
    };
  }, [studio]);

  const hint = useMemo<Hint | null>(() => {
    if (completed || !currentGroup) {
      return null;
    }

    const midis = currentGroupHints(currentGroup);

    if (midis.length === 0) {
      return null;
    }

    return {
      source: HINT_SOURCE,
      midis,
      voicing: [...currentGroup.midis],
    };
  }, [completed, currentGroup]);

  useHintPublisher(hint);

  const sequenceGroups = useMemo<SequenceGroup[]>(
    () => groups.map((g) => ({ midis: g.midis })),
    [groups],
  );

  const total = groups.length;
  const displayIndex = confirmedIndex ?? currentIndex;

  return {
    currentGroup,
    shownGroup: groups[displayIndex] ?? groups[groups.length - 1],
    currentIndex,
    total,
    // The group the player is *looking at*, not the count of those behind
    // them: `currentIndex` counts confirmations, so the panel opened on
    // "0 / 12" while the first group was on screen.
    position: Math.min(displayIndex + 1, total),
    percent: total === 0 ? 0 : Math.round((currentIndex / total) * 100),
    completed,
    passes,
    diagnosis,
    flashing: confirmedIndex !== null,
    held,
    runId,
    onRetry,
    onGroupConfirmed,
    onSequenceCompleted,
    onError,
    onCurrentIndexChange: setCurrentIndex,
    sequenceGroups,
    wrongGroupsThisPass: wrongGroupCount,
    retries,
    lastPass,
    resumeIndex,
    abandons,
  };
}

/** Gaps between consecutive instants, in order. */
function intervalsOf(times: readonly number[]): number[] {
  const out: number[] = [];

  for (let i = 1; i < times.length; i += 1) {
    out.push(times[i] - times[i - 1]);
  }

  return out;
}
